import { useEffect, useState } from "react";
import api, { apiError, formatEuro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const CATEGORIE = ["Base", "Proteina", "Topping", "Salsa", "Altro"];
const UNITA = ["g", "ml", "pz"];

const emptyForm = {
  nome: "", categoria: "Base", unita: "g", costo_per_unita: "", allergeni: "",
  kcal: "", proteine: "", carboidrati: "", grassi: "", scorta: "", soglia_minima: "",
};

export default function Ingredienti() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { user } = useAuth();
  const isAdmin = ["admin", "master"].includes(user?.role);

  const load = () => api.get("/ingredients").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      nome: i.nome, categoria: i.categoria, unita: i.unita, costo_per_unita: String(i.costo_per_unita),
      allergeni: (i.allergeni || []).join(", "), kcal: String(i.nutrizione.kcal), proteine: String(i.nutrizione.proteine),
      carboidrati: String(i.nutrizione.carboidrati), grassi: String(i.nutrizione.grassi),
      scorta: String(i.scorta), soglia_minima: String(i.soglia_minima),
    });
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    const payload = {
      nome: form.nome, categoria: form.categoria, unita: form.unita,
      costo_per_unita: parseFloat(form.costo_per_unita) || 0,
      allergeni: form.allergeni.split(",").map((a) => a.trim()).filter(Boolean),
      nutrizione: {
        kcal: parseFloat(form.kcal) || 0, proteine: parseFloat(form.proteine) || 0,
        carboidrati: parseFloat(form.carboidrati) || 0, grassi: parseFloat(form.grassi) || 0,
      },
      scorta: parseFloat(form.scorta) || 0, soglia_minima: parseFloat(form.soglia_minima) || 0,
    };
    if (!payload.nome) { toast.error("Il nome è obbligatorio"); return; }
    try {
      if (editing) {
        await api.put(`/ingredients/${editing.id}`, payload);
        toast.success("Ingrediente aggiornato");
      } else {
        await api.post("/ingredients", payload);
        toast.success("Ingrediente creato");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (i) => {
    if (!window.confirm(`Eliminare "${i.nome}"?`)) return;
    try {
      await api.delete(`/ingredients/${i.id}`);
      toast.success("Ingrediente eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unitLabel = (u) => (u === "pz" ? "per pz" : `per 100${u}`);

  return (
    <div className="space-y-6 fade-in" data-testid="ingredienti-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Ingredienti</h1>
          <p className="text-[#6B7280] mt-1">Valori nutrizionali per 100g/ml, costi e allergeni.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} data-testid="ingredient-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
            <Plus size={18} weight="bold" /> Nuovo ingrediente
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="ingredients-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Costo</TableHead>
              <TableHead>Kcal</TableHead>
              <TableHead>Prot.</TableHead>
              <TableHead>Carb.</TableHead>
              <TableHead>Grassi</TableHead>
              <TableHead>Allergeni</TableHead>
              <TableHead>Scorta</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} className="hover:bg-stone-50" data-testid={`ingredient-row-${i.id}`}>
                <TableCell className="font-medium">{i.nome}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-full">{i.categoria}</Badge></TableCell>
                <TableCell>{formatEuro(i.costo_per_unita)} <span className="text-xs text-[#6B7280]">{unitLabel(i.unita)}</span></TableCell>
                <TableCell>{i.nutrizione.kcal}</TableCell>
                <TableCell>{i.nutrizione.proteine}g</TableCell>
                <TableCell>{i.nutrizione.carboidrati}g</TableCell>
                <TableCell>{i.nutrizione.grassi}g</TableCell>
                <TableCell className="text-xs text-[#6B7280]">{(i.allergeni || []).join(", ") || "—"}</TableCell>
                <TableCell>
                  <Badge className={`rounded-full ${i.soglia_minima > 0 && i.scorta <= i.soglia_minima ? "bg-red-100 text-red-800 hover:bg-red-100" : "bg-green-100 text-green-800 hover:bg-green-100"}`}>
                    {i.scorta} {i.unita}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(i)} data-testid={`ingredient-edit-${i.id}`} className="hover:bg-stone-100">
                        <PencilSimple size={18} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`ingredient-delete-${i.id}`} className="hover:bg-red-50 text-[#D32F2F]">
                        <Trash size={18} />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="ingredient-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica ingrediente" : "Nuovo ingrediente"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.nome} onChange={set("nome")} data-testid="ingredient-name-input" placeholder="Es. Salmone fresco" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={form.categoria} onValueChange={set("categoria")}>
                <SelectTrigger data-testid="ingredient-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Unità di misura</Label>
              <Select value={form.unita} onValueChange={set("unita")}>
                <SelectTrigger data-testid="ingredient-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITA.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Costo € ({form.unita === "pz" ? "per pezzo" : `per 100${form.unita}`})</Label>
              <Input type="number" step="0.01" value={form.costo_per_unita} onChange={set("costo_per_unita")} data-testid="ingredient-cost-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Allergeni (separati da virgola)</Label>
              <Input value={form.allergeni} onChange={set("allergeni")} data-testid="ingredient-allergens-input" placeholder="Pesce, Soia" />
            </div>
            <div className="col-span-2">
              <p className="text-sm font-semibold mb-2">Valori nutrizionali ({form.unita === "pz" ? "per pezzo" : `per 100${form.unita}`})</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Kcal</Label><Input type="number" step="0.1" value={form.kcal} onChange={set("kcal")} data-testid="ingredient-kcal-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Proteine (g)</Label><Input type="number" step="0.1" value={form.proteine} onChange={set("proteine")} data-testid="ingredient-protein-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Carboidrati (g)</Label><Input type="number" step="0.1" value={form.carboidrati} onChange={set("carboidrati")} data-testid="ingredient-carbs-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Grassi (g)</Label><Input type="number" step="0.1" value={form.grassi} onChange={set("grassi")} data-testid="ingredient-fat-input" /></div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Scorta attuale ({form.unita})</Label>
              <Input type="number" step="0.1" value={form.scorta} onChange={set("scorta")} data-testid="ingredient-stock-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Soglia minima ({form.unita})</Label>
              <Input type="number" step="0.1" value={form.soglia_minima} onChange={set("soglia_minima")} data-testid="ingredient-threshold-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="ingredient-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
